import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Paper,
  Popper,
  styled,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StewartInput from "controls/global/stewart-input";
import ActionButton from "controls/global/action-button/ActionButton";
import { colors, fontWeight, zIndex } from "theme/defaultStyle";
import { getPolicyGlobalSearch } from "utils/api/policyTransactionApi";
import GlobalSearchResults from "./GlobalSearchResults";
import {
  buildSummary,
  EMPTY_GS_VALUES,
  GlobalSearchCriterion,
  GlobalSearchFieldKey,
  GlobalSearchQueueRow,
  GlobalSearchValues,
  GS_FIELDS,
  GS_PLACEHOLDER,
  isSearchEnabled,
  readCriteria,
  toQueueRows,
  toTabCriteria,
} from "./globalSearchConfig";

const BAR_WIDTH = 340;

const Trigger = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 7,
  width: BAR_WIDTH,
  height: 34,
  padding: "0 8px 0 10px",
  backgroundColor: colors.white,
  border: `1px solid ${colors.grey17}`,
  borderRadius: 4,
  cursor: "pointer",
  outline: "none",
  boxSizing: "border-box",
  "&:focus-visible, &.open": {
    borderColor: colors.blue15,
    boxShadow: "0 0 0 2px rgba(31,74,122,0.15)",
  },
  "& .gs-icon": { fontSize: 17, color: colors.grey15, flex: "0 0 auto" },
  "& .gs-summary": {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    color: colors.grey08,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "&.placeholder": { color: colors.grey15 },
  },
  "& .gs-caret": {
    fontSize: 20,
    color: colors.grey15,
    borderRadius: 3,
    transition: "transform 0.15s ease",
  },
  "&.open .gs-caret": { transform: "rotate(180deg)" },
});

const Panel = styled(Paper)({
  width: BAR_WIDTH,
  marginTop: 6,
  border: `1px solid ${colors.grey17}`,
  borderRadius: 6,
  boxShadow: "0 10px 30px rgba(16,32,64,0.18)",
  maxHeight: "80vh",
  overflowY: "auto",
  "& .gs-crit": {
    display: "grid",
    gridTemplateColumns: "104px 1fr",
    gap: 10,
    alignItems: "center",
    padding: "14px 16px 0",
  },
  "& .gs-crit-label": {
    fontSize: 11,
    fontWeight: fontWeight.bold2,
    color: colors.grey08,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  "& .gs-field div.MuiInputBase-root": { height: 32, backgroundColor: colors.white },
  "& .gs-field input.MuiInputBase-input": {
    height: 32,
    padding: "0 8px",
    fontSize: 12,
    boxSizing: "border-box",
  },
  "& .gs-crit-note": {
    padding: "10px 16px 0",
    fontSize: 10.5,
    color: colors.grey15,
    lineHeight: 1.5,
    fontStyle: "italic",
  },
  "& .gs-crit-actions": {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    padding: "12px 16px 14px",
    "& .MuiButton-root": { height: 30, fontSize: 12 },
  },
  "& .gs-clear": { textTransform: "none", color: colors.blue15 },
});

type Props = {
  // Page scope / State picker criteria (AgencyType, StateAbbrList) from buildPageCriteria.
  pageCriteria: Record<string, unknown>;
  onOpenQueue: (row: GlobalSearchQueueRow, criteria: Record<string, string>, label: string) => void;
  // Clear and Search both drop the filters a previous result applied to the queue tabs.
  onResetQueueFilters?: () => void;
};

type SearchState = {
  criteria: GlobalSearchCriterion[];
  values: GlobalSearchValues; // snapshot the rows were computed from
  rows: GlobalSearchQueueRow[] | null; // null = request failed
};

const GlobalSearchBar = ({ pageCriteria, onOpenQueue, onResetQueueFilters }: Props) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRefs = useRef<Partial<Record<GlobalSearchFieldKey, HTMLInputElement | null>>>({});
  const reqIdRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<GlobalSearchValues>(EMPTY_GS_VALUES);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState<SearchState | null>(null);
  const [showResults, setShowResults] = useState(false);
  // "Change search": criteria form shown above the current results.
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(0);

  const summary = buildSummary(values);
  const canSearch = isSearchEnabled(values) && !loading;

  const focusField = useCallback((key?: GlobalSearchFieldKey, select = false) => {
    // Wait a tick so the Popper content is mounted before focusing.
    setTimeout(() => {
      const target =
        (key && inputRefs.current[key]) ||
        GS_FIELDS.map((f) => inputRefs.current[f.key]).find((el) => el && !el.value) ||
        inputRefs.current[GS_FIELDS[0].key];
      target?.focus();
      if (select) target?.select();
    }, 0);
  }, []);

  const resetResults = useCallback(() => {
    reqIdRef.current++;
    setLoading(false);
    setSearch(null);
    setShowResults(false);
    setEditing(false);
  }, []);

  // Results were computed for the previous scope / states — drop them.
  useEffect(() => {
    resetResults();
  }, [pageCriteria, resetResults]);

  const handleToggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (!showResults) focusField();
  };

  const handleChange = (key: GlobalSearchFieldKey, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    // Keep the current results on screen until the next Search replaces them;
    // just drop any request still in flight for the old criteria.
    if (loading) {
      reqIdRef.current++;
      setLoading(false);
    }
  };

  const handleClear = () => {
    setValues(EMPTY_GS_VALUES);
    resetResults();
    onResetQueueFilters?.();
    focusField(GS_FIELDS[0].key);
  };

  const runSearch = async () => {
    if (!isSearchEnabled(values)) return;
    onResetQueueFilters?.();
    const criteria = readCriteria(values);
    const stateList = pageCriteria.StateAbbrList;
    const myReq = ++reqIdRef.current;
    setLoading(true);
    const result = await getPolicyGlobalSearch({
      ...Object.fromEntries(criteria.map((c) => [c.key, c.value])),
      AgencyType: (pageCriteria.AgencyType as string | undefined) || undefined,
      StateAbbrList: Array.isArray(stateList) && stateList.length ? stateList.join(",") : undefined,
    });
    if (reqIdRef.current !== myReq) return;
    const rows = result ? toQueueRows(result) : null;
    setLoading(false);
    setSearch({ criteria, values, rows });
    setSelected(Math.max(0, rows?.findIndex((r) => r.enabled) ?? 0));
    setShowResults(true);
    setEditing(false);
  };

  const handleChangeSearch = () => {
    setEditing(true);
    const firstFilled = GS_FIELDS.find((f) => values[f.key].trim())?.key;
    focusField(firstFilled, true);
  };

  const handleOpenQueue = (row: GlobalSearchQueueRow) => {
    if (!row.enabled || !search) return;
    setOpen(false);
    const label = search.criteria.map((c) => `${c.label} ${c.value}`).join(" + ");
    onOpenQueue(row, toTabCriteria(search.values), label);
  };

  const enabledRows = useMemo(
    () => (search?.rows ?? []).map((r, i) => (r.enabled ? i : -1)).filter((i) => i >= 0),
    [search]
  );

  const handlePanelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      anchorRef.current?.focus();
      return;
    }
    if (!showResults || !search?.rows || !enabledRows.length) return;
    if ((e.key === "ArrowDown" || e.key === "ArrowUp") && !(e.target instanceof HTMLInputElement)) {
      e.preventDefault();
      const pos = enabledRows.indexOf(selected);
      const step = e.key === "ArrowDown" ? 1 : -1;
      const next = enabledRows[(pos + step + enabledRows.length) % enabledRows.length];
      setSelected(next);
    } else if (e.key === "Enter" && !(e.target instanceof HTMLInputElement)) {
      const row = search.rows[selected];
      if (row) {
        e.preventDefault();
        handleOpenQueue(row);
      }
    }
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current?.contains(event.target as Node)) return;
    setOpen(false);
  };

  return (
    <>
      <Trigger
        ref={anchorRef}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={open ? "open" : ""}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <SearchIcon className="gs-icon" />
        <span className={`gs-summary${summary ? "" : " placeholder"}`} title={summary ?? GS_PLACEHOLDER}>
          {summary ?? GS_PLACEHOLDER}
        </span>
        <ExpandMoreIcon className="gs-caret" />
      </Trigger>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        disablePortal
        style={{ zIndex: zIndex.high2 }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Panel role="dialog" aria-label="Global search" onKeyDown={handlePanelKeyDown}>
            {(!showResults || !search || editing) && (
              <Box>
                {GS_FIELDS.map((f) => (
                  <Box className="gs-crit" key={f.key}>
                    <label className="gs-crit-label" htmlFor={`gs-${f.key}`}>
                      {f.label}
                    </label>
                    <StewartInput
                      className="gs-field"
                      id={`gs-${f.key}`}
                      fullWidth
                      autoComplete="off"
                      placeholder={f.placeholder}
                      value={values[f.key]}
                      inputRef={(el: HTMLInputElement | null) => {
                        inputRefs.current[f.key] = el;
                      }}
                      inputProps={{ spellCheck: false }}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (canSearch) runSearch();
                        }
                      }}
                    />
                  </Box>
                ))}
                <Box className="gs-crit-note">
                  Enter any combination. A record has to match every field you fill.
                  File Number and Policy Number need at least 3 characters.
                </Box>
                <Box className="gs-crit-actions">
                  <Button className="gs-clear" variant="text" onClick={handleClear}>
                    Clear
                  </Button>
                  <ActionButton
                    variant="outlined"
                    color="primary"
                    disabled={!canSearch}
                    startIcon={
                      loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />
                    }
                    onClick={runSearch}
                  >
                    Search
                  </ActionButton>
                </Box>
              </Box>
            )}
            {showResults && search && (
              <Box sx={editing ? { borderTop: `1px solid ${colors.grey17}` } : undefined}>
                <GlobalSearchResults
                  criteria={search.criteria}
                  rows={search.rows}
                  error={!search.rows}
                  selected={selected}
                  onHover={setSelected}
                  onOpenQueue={handleOpenQueue}
                  onChangeSearch={handleChangeSearch}
                  onRetry={runSearch}
                />
              </Box>
            )}
          </Panel>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default GlobalSearchBar;
